import React, { useState, useEffect, useCallback } from 'react';
import api from '../api/client';

const GRADE_LABELS = {1:'1年',2:'2年',3:'3年',4:'4年',5:'5年',6:'6年'};
const SCORE_COLORS = {1:'#ef4444',2:'#f97316',3:'#eab308',4:'#22c55e',5:'#3b82f6'};
const SCORE_LABELS = {1:'要支援',2:'努力中',3:'標準',4:'優秀',5:'卓越'};

export default function UnitManagerTab({ schoolType }) {
  const grades = schoolType === 'junior' ? [1,2,3] : [1,2,3,4,5,6];
  const [subjects, setSubjects] = useState([]);
  const [selectedGrade, setSelectedGrade] = useState('');
  const [selectedSubjectName, setSelectedSubjectName] = useState('');
  const [subjectNames, setSubjectNames] = useState([]);
  const [activeTab, setActiveTab] = useState('list');
  const [toast, setToast] = useState(null);
  const [completing, setCompleting] = useState(null);
  const [viewResult, setViewResult] = useState(null);
  const [evalResults, setEvalResults] = useState([]);
  const [editGoal, setEditGoal] = useState(null);
  const [newGoalText, setNewGoalText] = useState('');
  const [addingGoalFor, setAddingGoalFor] = useState(null);

  // 一括入力用
  const [bulkGrade, setBulkGrade] = useState('');
  const [bulkSubjectName, setBulkSubjectName] = useState('');
  const [bulkRows, setBulkRows] = useState([{unit_name:'',goals:['']},{unit_name:'',goals:['']},{unit_name:'',goals:['']}]);

  const showToast = (msg, type='success') => {
    setToast({msg, type});
    setTimeout(() => setToast(null), 4000);
  };

  const fetchSubjects = useCallback(async () => {
    try {
      const r = await api.get('/subjects');
      setSubjects(r.data);
      const names = [...new Set(r.data.map(s => s.subject_name))];
      setSubjectNames(names);
    } catch(e) { console.error(e); }
  }, []);

  useEffect(() => { fetchSubjects(); }, [fetchSubjects]);

  const filteredSubjects = subjects.filter(s =>
    (!selectedGrade || String(s.grade) === selectedGrade) &&
    (!selectedSubjectName || s.subject_name === selectedSubjectName)
  );

  const handleComplete = async (subject) => {
    if (!window.confirm(`「${subject.unit_name || subject.subject_name}」を終了してAI評価を実行しますか？`)) return;
    setCompleting(subject.id);
    try {
      const r = await api.patch(`/subjects/${subject.id}/complete`);
      showToast(`✓ 「${subject.unit_name || subject.subject_name}」のAI評価が完了しました`);
      fetchSubjects();
    } catch(e) {
      showToast('評価の実行に失敗しました', 'error');
    } finally { setCompleting(null); }
  };

  const handleReopen = async (subject) => {
    if (!window.confirm('この単元を未完了に戻しますか？')) return;
    try {
      await api.patch(`/subjects/${subject.id}/reopen`);
      fetchSubjects();
    } catch(e) {}
  };

  const handleViewResult = async (subject) => {
    try {
      const r = await api.get(`/subjects/${subject.id}/evaluation`);
      setEvalResults(r.data);
      setViewResult(subject);
    } catch(e) {}
  };

  const handleAddGoal = async (subjectId) => {
    if (!newGoalText.trim()) return;
    try {
      await api.post(`/subjects/${subjectId}/goals`, { goal_text: newGoalText });
      setNewGoalText('');
      setAddingGoalFor(null);
      fetchSubjects();
    } catch(e) {}
  };

  const handleDeleteGoal = async (subjectId, goalId) => {
    if (!window.confirm('この目標を削除しますか？')) return;
    try {
      await api.delete(`/subjects/${subjectId}/goals/${goalId}`);
      fetchSubjects();
    } catch(e) {}
  };

  const handleUpdateGoal = async (subjectId, goalId, text) => {
    try {
      await api.patch(`/subjects/${subjectId}/goals/${goalId}`, { goal_text: text });
      setEditGoal(null);
      fetchSubjects();
    } catch(e) {}
  };

  const handleBulkRegister = async () => {
    if (!bulkGrade || !bulkSubjectName) { alert('学年と教科名を入力してください'); return; }
    const units = bulkRows.filter(r => r.unit_name.trim()).map(r => ({
      unit_name: r.unit_name,
      goals: r.goals.filter(g => g.trim())
    }));
    if (units.length === 0) { alert('単元名を入力してください'); return; }
    try {
      await api.post('/subjects/bulk', { grade: parseInt(bulkGrade), subject_name: bulkSubjectName, units });
      showToast(`✓ ${units.length}件の単元を登録しました`);
      setBulkRows([{unit_name:'',goals:['']},{unit_name:'',goals:['']},{unit_name:'',goals:['']}]);
      setBulkSubjectName('');
      setBulkGrade('');
      fetchSubjects();
      setActiveTab('list');
    } catch(e) { alert('登録に失敗しました'); }
  };

  const handleDownloadAll = async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await fetch('/api/export/evaluations', {
        headers: { 'Authorization': 'Bearer ' + token }
      });
      if (!r.ok) { alert('出力に失敗しました'); return; }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'evaluation_all.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch(e) { alert('出力に失敗しました'); }
  };

  const groupByClass = (results) => {
    const groups = {};
    for (const r of results) {
      const key = `${r.grade}年${r.class_name}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(r);
    }
    return groups;
  };

  const S = {
    wrap: { padding: 20 },
    tabs: { display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid #e2e8f0' },
    tab: { padding: '8px 16px', fontSize: 13, cursor: 'pointer', color: '#666', background: 'none', border: 'none', borderBottom: '2px solid transparent' },
    tabActive: { padding: '8px 16px', fontSize: 13, cursor: 'pointer', color: '#2d4a8a', fontWeight: 600, background: 'none', border: 'none', borderBottom: '2px solid #2d4a8a' },
    card: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 20, marginBottom: 16 },
    btn: { padding: '6px 14px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, cursor: 'pointer', background: '#fff' },
    btnPrimary: { padding: '6px 14px', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer', background: '#2d4a8a', color: '#fff' },
    btnDanger: { padding: '6px 14px', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer', background: '#ef4444', color: '#fff' },
    btnSuccess: { padding: '6px 14px', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer', background: '#059669', color: '#fff' },
    btnSmall: { padding: '3px 8px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 11, cursor: 'pointer', background: '#fff' },
    input: { padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, width: '100%' },
    select: { padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 },
    label: { fontSize: 12, color: '#6b7280', marginBottom: 4, display: 'block' },
    toast: { position: 'fixed', top: 20, right: 20, zIndex: 9999, background: '#059669', color: '#fff', padding: '12px 20px', borderRadius: 8, fontSize: 14, fontWeight: 600, boxShadow: '0 4px 12px rgba(0,0,0,0.15)', display: 'flex', alignItems: 'center', gap: 8 },
    toastError: { background: '#ef4444' },
  };

  return (
    <div style={S.wrap}>
      {toast && (
        <div style={{ ...S.toast, ...(toast.type === 'error' ? S.toastError : {}) }}>
          {toast.type !== 'error' && '✓'} {toast.msg}
        </div>
      )}

      {/* 評価結果モーダル */}
      {viewResult && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, overflowY: 'auto', padding: 20 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, maxWidth: 800, margin: '20px auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: '#1e40af' }}>
                {viewResult.subject_name}「{viewResult.unit_name || viewResult.subject_name}」評価結果
              </h3>
              <button onClick={() => setViewResult(null)} style={{ ...S.btn, padding: '4px 10px' }}>閉じる</button>
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
              {[5,4,3,2,1].map(sc => (
                <div key={sc} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '6px 12px', textAlign: 'center', minWidth: 60 }}>
                  <div style={{ fontSize: 18, fontWeight: 700, color: SCORE_COLORS[sc] }}>{evalResults.filter(r => r.score === sc).length}</div>
                  <div style={{ fontSize: 10, color: '#6b7280' }}>{sc}({SCORE_LABELS[sc]})</div>
                </div>
              ))}
              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '6px 12px', textAlign: 'center', minWidth: 60 }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#9ca3af' }}>{evalResults.filter(r => !r.score).length}</div>
                <div style={{ fontSize: 10, color: '#6b7280' }}>未記録</div>
              </div>
            </div>
            {Object.entries(groupByClass(evalResults)).map(([cls, students]) => (
              <div key={cls} style={{ marginBottom: 20 }}>
                <div style={{ fontWeight: 700, color: '#1e40af', marginBottom: 8 }}>{cls}</div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: '#f1f5f9' }}>
                      <th style={{ padding: '6px 10px', textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>番号</th>
                      <th style={{ padding: '6px 10px', textAlign: 'center', borderBottom: '2px solid #e2e8f0' }}>回数</th>
                      <th style={{ padding: '6px 10px', textAlign: 'center', borderBottom: '2px solid #e2e8f0' }}>評価</th>
                      <th style={{ padding: '6px 10px', textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>指導アドバイス</th>
                      <th style={{ padding: '6px 10px', textAlign: 'left', borderBottom: '2px solid #e2e8f0', width: 200 }}>評価根拠（引用）</th>
                    </tr>
                  </thead>
                  <tbody>
                    {students.map(r => (
                      <tr key={r.student_id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '8px 10px' }}>{r.number}番</td>
                        <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                          <span style={{ fontSize: 12, color: r.review_count > 0 ? '#374151' : '#9ca3af' }}>{r.review_count}回</span>
                        </td>
                        <td style={{ padding: '8px 10px', textAlign: 'center', fontSize: 12, fontWeight: r.score === 1 ? 700 : 500, color: r.score === 1 ? '#ef4444' : r.score ? SCORE_COLORS[r.score] : '#9ca3af' }}>
                          {r.score ? SCORE_LABELS[r.score] : '-'}
                        </td>
                        <td style={{ padding: '8px 10px', fontSize: 12, color: '#374151' }}>{r.comment || '-'}</td>
                        <td style={{ padding: '8px 10px', fontSize: 11, color: '#6b7280' }}>{r.basis || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
            <button style={S.btnSuccess} onClick={handleDownloadAll}>CSVで保存</button>
          </div>
        </div>
      )}

      <div style={S.tabs}>
        {[['list','単元一覧・終了管理'],['bulk','一括入力（学期初め）']].map(([k,v]) => (
          <button key={k} style={activeTab===k ? S.tabActive : S.tab} onClick={() => setActiveTab(k)}>{v}</button>
        ))}
      </div>

      {activeTab === 'list' && (
        <>
          <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div>
              <label style={S.label}>学年で絞り込み</label>
              <select style={S.select} value={selectedGrade} onChange={e => setSelectedGrade(e.target.value)}>
                <option value="">全学年</option>
                {grades.map(g => <option key={g} value={g}>{g}年</option>)}
              </select>
            </div>
            <div>
              <label style={S.label}>教科で絞り込み</label>
              <select style={S.select} value={selectedSubjectName} onChange={e => setSelectedSubjectName(e.target.value)}>
                <option value="">全教科</option>
                {subjectNames.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <button style={S.btnSuccess} onClick={handleDownloadAll}>📥 全評価済みCSV出力</button>
          </div>

          {filteredSubjects.length === 0 && (
            <div style={{ color: '#9ca3af', fontSize: 14, padding: 20, textAlign: 'center' }}>
              単元がありません。「一括入力」タブから単元を登録してください。
            </div>
          )}

          {filteredSubjects.map(s => (
            <div key={s.id} style={{ ...S.card, borderLeft: `4px solid ${s.is_completed ? '#059669' : '#e2e8f0'}` }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <span style={{ background: '#dbeafe', color: '#1e40af', padding: '2px 8px', borderRadius: 99, fontSize: 12, fontWeight: 600 }}>{s.grade}年</span>
                    <span style={{ background: '#f1f5f9', color: '#475569', padding: '2px 8px', borderRadius: 99, fontSize: 12 }}>{s.subject_name}</span>
                    <span style={{ fontSize: 15, fontWeight: 700, color: '#1e293b' }}>{s.unit_name || s.subject_name}</span>
                    {s.is_completed && <span style={{ background: '#d1fae5', color: '#065f46', padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 600 }}>✓ 評価済み</span>}
                  </div>

                  {/* 目標一覧 */}
                  <div style={{ marginLeft: 8 }}>
                    {s.goals && s.goals.length > 0 ? s.goals.map((g, i) => (
                      <div key={g.id} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                        <span style={{ fontSize: 11, color: '#9ca3af', minWidth: 16 }}>目標{i+1}</span>
                        {editGoal === g.id ? (
                          <>
                            <input defaultValue={g.goal_text} id={`goal-${g.id}`} style={{ ...S.input, flex: 1, fontSize: 12 }} />
                            <button style={S.btnPrimary} onClick={() => handleUpdateGoal(s.id, g.id, document.getElementById(`goal-${g.id}`).value)}>保存</button>
                            <button style={S.btn} onClick={() => setEditGoal(null)}>取消</button>
                          </>
                        ) : (
                          <>
                            <span style={{ fontSize: 13, color: '#374151', flex: 1 }}>{g.goal_text}</span>
                            <button style={S.btnSmall} onClick={() => setEditGoal(g.id)}>編集</button>
                            <button style={{ ...S.btnSmall, color: '#ef4444', borderColor: '#ef4444' }} onClick={() => handleDeleteGoal(s.id, g.id)}>削除</button>
                          </>
                        )}
                      </div>
                    )) : (
                      <span style={{ fontSize: 12, color: '#9ca3af' }}>目標未設定</span>
                    )}
                    {addingGoalFor === s.id ? (
                      <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                        <input value={newGoalText} onChange={e => setNewGoalText(e.target.value)} placeholder="目標を入力" style={{ ...S.input, flex: 1, fontSize: 12 }} />
                        <button style={S.btnPrimary} onClick={() => handleAddGoal(s.id)}>追加</button>
                        <button style={S.btn} onClick={() => { setAddingGoalFor(null); setNewGoalText(''); }}>取消</button>
                      </div>
                    ) : (
                      <button style={{ ...S.btnSmall, marginTop: 4, color: '#2d4a8a', borderColor: '#2d4a8a' }} onClick={() => { setAddingGoalFor(s.id); setNewGoalText(''); }}>＋ 目標を追加</button>
                    )}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
                  {s.is_completed ? (
                    <>
                      <button style={S.btn} onClick={() => handleViewResult(s)}>📊 結果を見る</button>
                      <button style={{ ...S.btnSmall, color: '#6b7280' }} onClick={() => handleReopen(s)}>再開</button>
                    </>
                  ) : (
                    <button
                      style={{ ...S.btnPrimary, background: completing === s.id ? '#9ca3af' : '#2d4a8a' }}
                      disabled={completing === s.id}
                      onClick={() => handleComplete(s)}
                    >
                      {completing === s.id ? 'AI評価中...' : '✓ 単元終了・AI評価'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </>
      )}

      {activeTab === 'bulk' && (
        <div style={S.card}>
          <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>単元の一括登録</h3>
          <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>学期初めに単元をまとめて登録できます。目標は後から追加・編集できます。</p>

          <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
            <div>
              <label style={S.label}>学年 <span style={{ color: '#ef4444' }}>※必須</span></label>
              <select style={S.select} value={bulkGrade} onChange={e => setBulkGrade(e.target.value)}>
                <option value="">選択</option>
                {grades.map(g => <option key={g} value={g}>{g}年</option>)}
              </select>
            </div>
            <div style={{ flex: 1, maxWidth: 200 }}>
              <label style={S.label}>教科名 <span style={{ color: '#ef4444' }}>※必須</span></label>
              <input style={S.input} placeholder="例：算数" value={bulkSubjectName} onChange={e => setBulkSubjectName(e.target.value)} />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 36px', gap: 6, marginBottom: 6 }}>
            <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>単元名 ※必須</div>
            <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>目標1（省略可）</div>
            <div></div>
          </div>

          {bulkRows.map((row, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 36px', gap: 6, marginBottom: 6, alignItems: 'center' }}>
              <input style={S.input} placeholder={`例：単元${i+1}`} value={row.unit_name} onChange={e => {
                const r = [...bulkRows]; r[i] = { ...r[i], unit_name: e.target.value }; setBulkRows(r);
              }} />
              <input style={S.input} placeholder="（後で入力してもOK）" value={row.goals[0] || ''} onChange={e => {
                const r = [...bulkRows]; r[i] = { ...r[i], goals: [e.target.value] }; setBulkRows(r);
              }} />
              <button style={{ ...S.btn, padding: '6px 8px', color: '#9ca3af' }} onClick={() => setBulkRows(bulkRows.filter((_,j) => j !== i))}>×</button>
            </div>
          ))}

          <button style={{ ...S.btn, width: '100%', marginBottom: 16, borderStyle: 'dashed', color: '#6b7280' }}
            onClick={() => setBulkRows([...bulkRows, { unit_name: '', goals: [''] }])}>
            ＋ 行を追加
          </button>

          <div style={{ background: '#f8fafc', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#6b7280', marginBottom: 16 }}>
            登録予定：{bulkGrade && `${bulkGrade}年`} {bulkSubjectName} {bulkRows.filter(r => r.unit_name).length}単元
          </div>

          <button style={S.btnPrimary} onClick={handleBulkRegister}>まとめて登録する</button>
        </div>
      )}
    </div>
  );
}
